<template>
  <div class="plan-card-wrapper" :class="{ 'is-mockup': isMockup }">
    <!-- Estilo mockup HMP 2027: navy + beneficios + CTA -->
    <article
      v-if="isMockup"
      class="hmp-plan-card animate-on-scroll fade-in-up"
      :class="{ 'is-visible': isVisible, 'is-featured': !!plan.catalogBadge }"
      :style="`animation-delay: ${0.1 + index * 0.1}s`"
    >
      <div class="hmp-plan-top">
        <span v-if="plan.catalogBadge" class="hmp-badge">{{ plan.catalogBadge }}</span>
        <h3 class="hmp-name">{{ plan.name }}</h3>
        <div class="hmp-price">
          <template v-if="isFamiliarPlan">
            <span class="hmp-price-from">desde</span>
            <span class="hmp-amount">${{ familiarFromPrice }}</span>
            <small>/mes</small>
          </template>
          <template v-else>
            <span class="hmp-amount">${{ displayPrice }}</span>
            <small>{{ priceUnit }}</small>
          </template>
        </div>
        <p v-if="wasLine" class="hmp-was">{{ wasLine }}</p>
        <p v-if="plan.catalogFor" class="hmp-for">{{ plan.catalogFor }}</p>
      </div>
      <ul class="hmp-benefits">
        <li v-for="(b, i) in mockupBenefits" :key="i">
          <b>{{ b[0] }}</b>
          <span v-if="b[1]">{{ b[1] }}</span>
        </li>
      </ul>
      <button
        v-if="purchaseDisabled"
        type="button"
        class="btn hmp-cta hmp-cta--soon"
        disabled
      >
        Próximamente
      </button>
      <button
        v-else
        type="button"
        class="btn hmp-cta"
        @click="goToPurchase"
      >
        Quiero este plan <i class="fas fa-chevron-right ml-1"></i>
      </button>
    </article>

    <template v-else>
      <div
        class="detailed-plan-card animate-on-scroll fade-in-up"
        :class="{
          'is-visible': isVisible,
          'plan-with-gradient': index % 2 === 1
        }"
        :style="`animation-delay: ${0.1 + index * 0.15}s`"
      >
        <div v-if="plan.status === 1" class="plan-badge">
          <i class="fas fa-check-circle mr-1"></i>
          Disponible
        </div>
        <div class="plan-header">
          <h3 class="plan-name">{{ plan.name }}</h3>
          <div class="plan-price-large">
            <span class="currency">$</span>
            <span class="amount">{{ plan.price }}</span>
            <span class="period">/Mes</span>
          </div>
          <div class="plan-meta">
            <span class="meta-item">
              <i class="fas fa-calendar-alt mr-1"></i>
              {{ plan.validity }} meses
            </span>
            <span class="meta-item">
              <i class="fas fa-tag mr-1"></i>
              {{ plan.membership_type === 0 ? 'Membresía' : plan.membership_type === 1 ? 'Plan Médico' : 'N/A' }}
            </span>
          </div>
        </div>

        <div class="plan-body">
          <div class="plan-statistics">
            <div class="stat-item">
              <div class="stat-number">{{ plan.facilities ? plan.facilities.length : 0 }}</div>
              <div class="stat-label">Clínicas</div>
              <button
                v-if="plan.facilities && plan.facilities.length > 0"
                class="btn btn-sm btn-outline-primary mt-2"
                @click="$emit('show-facilities', plan)"
              >
                <i class="fas fa-list mr-1"></i>
                Ver Lista
              </button>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ benefitsCount }}</div>
              <div class="stat-label">Beneficios</div>
              <button
                v-if="hasBenefits"
                class="btn btn-sm btn-outline-primary mt-2"
                @click="$emit('show-procedures', plan)"
              >
                <i class="fas fa-list mr-1"></i>
                Ver Detalles
              </button>
            </div>
          </div>

          <ul v-if="plan.marketingBenefits && plan.marketingBenefits.length" class="plan-mkt-benefits">
            <li v-for="(b, i) in plan.marketingBenefits.slice(0, 4)" :key="i">
              <i class="fas fa-check"></i>
              <span>{{ b[0] }}</span>
            </li>
          </ul>

          <div v-if="starsIcon" class="plan-stars">
            <img :src="starsIcon" alt="Rating" class="stars-image" />
          </div>
        </div>
      </div>
      <button
        v-if="purchaseDisabled"
        class="btn btn-primary btn-block plan-action-btn"
        disabled
      >
        Próximamente
      </button>
      <button
        v-else
        class="btn btn-primary btn-block btn-shimmer plan-action-btn"
        @click="goToPurchase"
      >
        <i class="fas fa-shopping-cart mr-2"></i>
        Obtener Plan
      </button>
    </template>
  </div>
</template>

<script>
import ToastificationContent from '@/core/plugins/toastification/ToastificationContent.vue'
import { famPrice, isOnlinePurchaseDisabled } from '../data/hmpPlans2027'

/**
 * Tarjeta de plan (mockup o clásica). CTA navega a /comprar-plan/:id.
 */
export default {
  name: 'PlanDetailCard',
  props: {
    plan: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      default: 0
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    starsIcon: {
      type: String,
      default: null
    },
    /**
     * Cuando es true, no navega a compra: emite add-plan (panel miembro).
     */
    simplePurchase: {
      type: Boolean,
      default: false
    },
    /**
     * `mockup`: tarjeta estilo HMP 2027 (navy + beneficios).
     * `default`: tarjeta clásica con clínicas / estadísticas.
     */
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'mockup'].includes(v)
    }
  },
  computed: {
    isMockup() {
      return this.variant === 'mockup'
    },
    benefitsCount() {
      if (this.plan.marketingBenefits && this.plan.marketingBenefits.length) {
        return this.plan.marketingBenefits.length
      }
      return this.plan.parameters_details ? this.plan.parameters_details.length : 0
    },
    hasBenefits() {
      return this.benefitsCount > 0
    },
    isFamiliarPlan() {
      return this.plan.catalogId === 'familiar'
        || /familiar|familia/i.test(String(this.plan.name || ''))
    },
    familiarFromPrice() {
      return famPrice(2)
    },
    displayPrice() {
      return this.plan.price
    },
    priceUnit() {
      if (this.plan.catalogUnit) return this.plan.catalogUnit
      return '/mes'
    },
    wasLine() {
      if (this.plan.catalogWas) return `Antes $${this.plan.catalogWas}`
      return ''
    },
    mockupBenefits() {
      const list = this.plan.marketingBenefits
      if (Array.isArray(list) && list.length) return list.slice(0, 5)
      return []
    },
    purchaseDisabled() {
      return this.plan.purchaseDisabled === true
        || isOnlinePurchaseDisabled(this.plan.catalogId)
    }
  },
  methods: {
    goToPurchase() {
      if (this.simplePurchase) {
        this.$emit('add-plan', this.plan)
        return
      }
      if (this.purchaseDisabled) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Próximamente',
            icon: 'AlertCircleIcon',
            variant: 'info',
            text: 'Este plan aún no está disponible para pago en línea.'
          }
        })
        return
      }
      if (!this.plan || !this.plan.id) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Plan no disponible',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'Este plan aún no está disponible para compra en línea.'
          }
        })
        return
      }
      this.$router.push({
        name: 'comprar-plan',
        params: { planId: String(this.plan.id) }
      }).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/variables';

.plan-card-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;

  &.is-mockup {
    height: 100%;
  }
}

/* Tarjeta estilo mockup HMP 2027 */
.hmp-plan-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: $landing-bg-white;
  border: 1px solid $landing-border-light;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(13, 43, 69, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(13, 43, 69, 0.14);
  }

  &.is-featured {
    border-color: $landing-secondary-color;
    box-shadow: 0 12px 32px rgba(34, 207, 224, 0.22);
  }
}

.hmp-plan-top {
  background: $landing-primary-color;
  color: #f3f7f8;
  padding: 1.25rem 1.15rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-height: 210px;
}

.hmp-badge {
  align-self: flex-start;
  background: $landing-secondary-color;
  color: $landing-primary-color;
  font-weight: 700;
  font-size: 0.72rem;
  border-radius: 999px;
  padding: 3px 10px;
  margin-bottom: 0.15rem;
}

.hmp-name {
  font-size: 1.2rem;
  font-weight: 800;
  margin: 0;
  line-height: 1.15;
  color: #fff;
}

.hmp-price {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;

  .hmp-price-from {
    font-size: 0.8rem;
    color: #b9cad6;
    font-weight: 600;
  }

  .hmp-amount {
    font-size: clamp(2rem, 2.4vw, 2.75rem);
    font-weight: 900;
    line-height: 0.95;
    letter-spacing: -0.02em;
  }

  small {
    font-size: 0.8rem;
    font-weight: 600;
    color: #b9cad6;
  }
}

.hmp-was {
  margin: 0;
  font-size: 0.8rem;
  color: #b9cad6;
}

.hmp-for {
  margin: 0.35rem 0 0;
  padding-top: 0.65rem;
  border-top: 1px solid #2c4a66;
  font-size: 0.82rem;
  line-height: 1.4;
  color: #e2edf2;
}

.hmp-benefits {
  list-style: none;
  margin: 0;
  padding: 0.75rem 1.1rem 0.5rem;
  flex: 1;

  li {
    padding: 0.55rem 0;
    border-bottom: 1px solid $landing-border-light;

    &:last-child {
      border-bottom: 0;
    }
  }

  b {
    display: block;
    font-size: 0.88rem;
    color: $landing-text-dark;
    font-weight: 700;
    line-height: 1.25;
  }

  span {
    display: block;
    margin-top: 0.15rem;
    font-size: 0.75rem;
    color: $landing-text-medium;
    line-height: 1.35;
  }
}

.hmp-cta--soon {
  opacity: 0.85;
  cursor: not-allowed;
  background: rgba(255, 255, 255, 0.2) !important;
  color: #e2edf2 !important;
}

.hmp-cta {
  margin: 0.5rem 1.1rem 1.1rem;
  border: 0;
  border-radius: 12px;
  background: $landing-secondary-color;
  color: $landing-primary-color;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.7rem 0.9rem;
  width: calc(100% - 2.2rem);
  transition: background 0.2s ease, transform 0.2s ease;

  i {
    color: $landing-primary-color !important;
  }

  &:hover {
    background: $landing-blue-light;
    color: $landing-primary-color;
    transform: translateY(-1px);

    i {
      color: $landing-primary-color !important;
    }
  }
}

.detailed-plan-card {
  background: linear-gradient(135deg, #ffffff 0%, #F8F9FA 100%);
  border: 2px solid #0d2b45;
  border-radius: 15px;
  padding: 2rem;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 1;
  display: flex;
  flex-direction: column;
  
  &.plan-with-gradient {
    background: #ffffff;
    border-color: #0d2b45;
    
    .plan-header {
      background: linear-gradient(to right, #123a5c 0%, #22cfe0 100%);
      border-radius: 15px 15px 0 0;
      margin: -2rem -2rem 2rem -2rem;
      padding: 2rem 2rem 1.5rem 2rem;
      border-bottom: 3px solid #0d2b45;
      color: white;
      
      .plan-name {
        color: white;
        font-size: 2.5rem;
        margin-bottom: 1.5rem;
        margin-top: 1rem;
      }
      
      .plan-price-large {
        display: flex;
        align-items: baseline;
        justify-content: center;
        
        .currency {
          font-size: 3.5rem;
          color: white;
          line-height: 1;
          vertical-align: baseline;
        }
        
        .amount {
          font-size: 7rem;
          color: white;
          line-height: 1;
        }
        
        .period {
          font-size: 2rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1;
          vertical-align: baseline;
        }
      }
      
      .plan-meta {
        .meta-item {
          color: rgba(255, 255, 255, 0.9);
          
          i {
            color: rgba(255, 255, 255, 0.8);
          }
        }
      }
    }
    
    .plan-body {
      .plan-statistics {
        .stat-item {
          background: #F8F9FA;
          
          &:hover {
            background: #E8F4F8;
          }
          
          .stat-number {
            color: #0d2b45;
          }
          
          .stat-label {
            color: #5A6D70;
          }
          
          .btn-outline-primary {
            border-color: #0d2b45;
            color: #0d2b45;
            display: inline-flex;
            align-items: center;
            white-space: nowrap;
            
            i {
              margin-right: 0.5rem;
            }
            
            &:hover {
              background: #0d2b45;
              color: white;
              
              i {
                margin-right: 0.5rem;
              }
            }
          }
        }
      }

      .plan-mkt-benefits {
        list-style: none;
        margin: 1rem 0 0;
        padding: 0;
        text-align: left;

        li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-bottom: 0.45rem;
          font-size: 0.88rem;
          color: #334155;
          line-height: 1.35;

          i {
            color: #22cfe0;
            margin-top: 0.15rem;
            flex-shrink: 0;
          }
        }
      }
      
      .btn-primary {
        background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%);
        color: white;
        border-color: #0d2b45;
        
        &:hover {
          background: linear-gradient(135deg, #123a5c 0%, #0d2b45 100%);
          color: white;
        }
      }
    }
  }
  
  &:hover {
    border-color: #0d2b45;
    box-shadow: 0 15px 40px rgba(13, 43, 69, 0.85);
    transform: translateY(-8px);
    background: linear-gradient(135deg, #ffffff 0%, #E8F4F8 100%);
    
    &.plan-with-gradient {
      background: #ffffff;
      
      .plan-header {
        background: linear-gradient(to right, #123a5c 0%, #22cfe0 100%);
      }
    }
  }
  
  .plan-badge {
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    background: #FFFFFF;
    color: #0d2b45;
    border: 2px solid #0d2b45;
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 1.1rem;
    font-weight: 700;
    box-shadow: 0 4px 12px rgba(13, 43, 69, 0.2);
    letter-spacing: 0.3px;
    z-index: 2;
    
    i {
      font-size: 1.1rem;
      color: #0d2b45;
    }
  }
  
  .plan-header {
    text-align: center;
    margin-bottom: 2rem;
    margin-top: 2rem;
    padding-bottom: 1.5rem;
    padding-top: 2rem;
    border-bottom: 3px solid #0d2b45;
    
    .plan-name {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2D4A4D;
      margin-bottom: 1.5rem;
      margin-top: 1rem;
    }
    
    .plan-price-large {
      margin-bottom: 1rem;
      display: flex;
      align-items: baseline;
      justify-content: center;
      
      .currency {
        font-size: 3.5rem;
        vertical-align: baseline;
        color: #0d2b45;
        line-height: 1;
      }
      
      .amount {
        font-size: 7rem;
        font-weight: 700;
        color: #0d2b45;
        line-height: 1;
      }
      
      .period {
        font-size: 2rem;
        color: #5A6D70;
        line-height: 1;
        vertical-align: baseline;
      }
    }
    
    .plan-meta {
      display: flex;
      justify-content: center;
      gap: 1.5rem;
      flex-wrap: wrap;
      
      .meta-item {
        color: #5A6D70;
        font-size: 0.9rem;
        
        i {
          color: #0d2b45;
        }
      }
    }
  }
  
  .plan-body {
    .plan-statistics {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;
      margin-bottom: 1.5rem;
      align-items: stretch;
      
      .stat-item {
        text-align: center;
        padding: 1.5rem;
        background: #F8F9FA;
        border-radius: 10px;
        transition: all 0.3s ease;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        min-height: 180px;
        
        &:hover {
          background: #E8F4F8;
          transform: translateY(-2px);
        }
        
        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #0d2b45;
          line-height: 1;
          margin-bottom: 0.5rem;
        }
        
        .stat-label {
          font-size: 0.9rem;
          color: #5A6D70;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          min-height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1.3;
        }
        
        .btn-outline-primary {
          margin-top: auto;
          width: 100%;
          justify-content: center;
        }
      }
    }
    
    .plan-stars {
      text-align: center;
      margin-top: 1.5rem;
      margin-bottom: 1rem;
      
      .stars-image {
        width: 100%;
        max-width: 200px;
        height: auto;
        object-fit: contain;
      }
    }
  }
}

.plan-action-btn {
  background: linear-gradient(135deg, #0d2b45 0%, #22cfe0 100%) !important;
  color: white !important;
  border: none !important;
  padding: 16px 32px !important;
  font-size: 1.2rem !important;
  border-radius: 12px !important;
  margin-top: 1rem;
  width: 100%;
  font-weight: 700 !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(13, 43, 69, 0.4) !important;
  }
}

/* Modal wizard compra (mismo en Home y Plans) */
.purchase-wizard-body {
  padding: 1.5rem 2rem;
}

.purchase-wizard-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.purchase-wizard-step-dot {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #E9ECEF;
  color: #6C757D;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &.active {
    background: $landing-primary-color;
    color: white;
  }
}

.purchase-wizard-step-line {
  width: 3rem;
  height: 3px;
  background: #E9ECEF;
  border-radius: 2px;
}

.purchase-wizard-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: $landing-primary-color;
}

.purchase-wizard-subtitle {
  font-size: 1rem;
  line-height: 1.5;
}

.purchase-wizard-steps-list {
  padding-left: 1.25rem;
  margin-bottom: 0;
  font-size: 1rem;
  line-height: 2;

  li {
    margin-bottom: 0.5rem;
  }
}

.purchase-wizard-link {
  color: $landing-primary-color;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: $landing-secondary-color;
  }
}
</style>